import { FormEvent, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link, Navigate, useNavigate } from "react-router-dom"
import { setAuthHeader } from "@/api/service"
import { ROUTES } from "@/helpers/routes"
import { showToast } from "@/helpers/toast"
import { useAppDispatch, useAppSelector } from "@/store"
import {
  useSendEmailOtpCreateMutation,
  useSendEmailOtpLoginMutation,
  useVerifyEmailOtpMutation
} from "@/store/apis/authAPI"
import { PlayerSelectors, setIsAuthorized } from "@/store/slices/player"
import { Button } from "@/ui/Button"
import styles from "./AuthPanel.module.scss"
import { authErrorMessageKey, isValidEmail } from "./helpers"

export type AuthPanelMode = "login" | "register"

type OAuthProvider = "google" | "yandex" | "telegram"

interface IAuthPanelProps {
  mode: AuthPanelMode
}

type Step = "email" | "code"

export const AuthPanel = ({ mode }: IAuthPanelProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const isAuthorized = useAppSelector(PlayerSelectors.selectIsAuthorized())

  const [sendEmailOtpLogin, sendLoginState] = useSendEmailOtpLoginMutation()
  const [sendEmailOtpCreate, sendCreateState] = useSendEmailOtpCreateMutation()
  const [verifyEmailOtp, verifyState] = useVerifyEmailOtpMutation()

  const [step, setStep] = useState<Step>("email")
  const [email, setEmail] = useState("")
  const [code, setCode] = useState("")

  if (isAuthorized) {
    return <Navigate to={ROUTES.HOME} replace />
  }

  const isLogin = mode === "login"
  const isSending = sendLoginState.isLoading || sendCreateState.isLoading
  const isVerifying = verifyState.isLoading

  const handleOAuthRedirect = (provider: OAuthProvider) => {
    window.location.href = `${import.meta.env.VITE_BACKEND}/auth/${provider}/start`
  }

  const handleSendCode = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const normalized = email.trim().toLowerCase()

    if (!isValidEmail(normalized)) {
      showToast.error(t("auth.errors.invalidEmail"))
      return
    }

    try {
      const request = isLogin ? sendEmailOtpLogin : sendEmailOtpCreate
      await request({ email: normalized }).unwrap()
      setEmail(normalized)
      setStep("code")
      showToast.info(t("auth.codeSent"))
    } catch (err) {
      const status = (err as { status?: number }).status
      showToast.error(t(authErrorMessageKey(status)))
    }
  }

  const handleVerifyCode = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!/^\d{6}$/.test(code)) {
      showToast.error(t("auth.errors.invalidCode"))
      return
    }
    try {
      const tokens = await verifyEmailOtp({ email, code }).unwrap()
      setAuthHeader(tokens.accessToken, tokens.refreshToken)
      dispatch(setIsAuthorized(true))
      navigate(ROUTES.HOME, { replace: true })
    } catch (err) {
      const status = (err as { status?: number }).status
      showToast.error(t(authErrorMessageKey(status)))
    }
  }

  const goBackToEmail = () => {
    setStep("email")
    setCode("")
  }

  return (
    <div className={styles.panel}>
      <h1 className={styles.title}>Overworld</h1>
      <h2 className={styles.subtitle}>
        {isLogin ? t("auth.titleLogin") : t("auth.titleRegister")}
      </h2>

      {step === "email" ? (
        <form className={styles.form} onSubmit={handleSendCode}>
          <input
            className={styles.input}
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder={t("auth.emailPlaceholder")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isSending}
            required
          />
          <Button type="submit" disabled={isSending}>
            {t("auth.sendCode")}
          </Button>
        </form>
      ) : (
        <form className={styles.form} onSubmit={handleVerifyCode}>
          <p className={styles.hint}>{t("auth.codeSent")}</p>
          <input
            className={styles.input}
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="\d{6}"
            maxLength={6}
            placeholder={t("auth.codePlaceholder")}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            disabled={isVerifying}
            required
          />
          <Button type="submit" disabled={isVerifying}>
            {t("auth.verifyCode")}
          </Button>
          <button
            type="button"
            className={styles.linkButton}
            onClick={goBackToEmail}
            disabled={isVerifying}
          >
            {t("auth.changeEmail")}
          </button>
        </form>
      )}

      <div className={styles.divider}>{t("auth.or")}</div>

      <div className={styles.oauth}>
        <Button onClick={() => handleOAuthRedirect("telegram")}>
          {isLogin
            ? t("auth.loginWithTelegram")
            : t("auth.registerWithTelegram")}
        </Button>
        <Button onClick={() => handleOAuthRedirect("google")}>
          {isLogin ? t("auth.loginWithGoogle") : t("auth.registerWithGoogle")}
        </Button>
        <Button onClick={() => handleOAuthRedirect("yandex")}>
          {isLogin ? t("auth.loginWithYandex") : t("auth.registerWithYandex")}
        </Button>
      </div>

      <Link
        className={styles.switchLink}
        to={isLogin ? ROUTES.REGISTER : ROUTES.LOGIN}
      >
        {isLogin ? t("auth.noAccount") : t("auth.haveAccount")}
      </Link>
    </div>
  )
}
